import { useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { TkticSelect } from "@/components/ui/TkticSelect";
import {
  COACH_TEMPLATES,
  COACH_TEMPLATE_GROUPS,
  findCoachTemplate,
  templatesInGroup,
  type CoachTemplate,
} from "@/lib/canvas/templates";
import { coerceNoteCategory, type ComposerValues } from "./Composer";
import { ConfirmDialog } from "@/components/ConfirmDialog";

/**
 * Template selector — picks one of the 14 Phase 2 templates and
 * applies it to the composer form.
 *
 * Overwrite-protect flow (mirrors legacy review.js:2393-2439):
 *   1. The "Apply" button reads the composer's current values.
 *   2. Detects "manually edited" fields: non-empty AND != the
 *      previously-applied template's value at that field.
 *   3. Category/tone defaults (category="other", note_type=
 *      "correction") count as "untouched" on a fresh composer —
 *      that's the initial state, so applying the first template
 *      shouldn't trigger a confirm.
 *   4. If anything was manually edited, `window.confirm` asks
 *      before clobbering. Proceed only if confirmed.
 *   5. On apply: write the template's structured fields into the
 *      composer + open the structured-feedback disclosure (parent
 *      shell handles that via the `onApply` callback). Store the
 *      applied id so the NEXT apply uses it as the reference for
 *      the manual-edit check.
 *
 * "Clear" resets the selector to empty + forgets the last-applied
 * id. Does NOT touch the composer fields (the coach's text stays).
 *
 * Active-template id is also reset on save (the parent shell wires
 * this; the selector exposes a `clear()` ref method if needed).
 */

export type TemplateSelectorProps = {
  /** Read the composer's current values. Provided by the parent so
   * the selector doesn't depend on a specific form library. Called
   * synchronously at Apply time. */
  getCurrentValues: () => Partial<ComposerValues>;
  /** Write the template's structured fields into the composer.
   * Parent decides which fields are template-controllable (title,
   * note_type, category, player_summary, what_happened, etc.). */
  onApply: (
    next: Partial<ComposerValues> & { __appliedTemplateId: string },
  ) => void;
};

/** Fields the templates write into the composer. coach_private_note
 * is INTENTIONALLY NOT in this set — see the registry doc comment.
 * `tags_raw` is a string mirror of the template's `tags: string[]`;
 * the comparison in `manuallyEdited` joins the array with ", " to
 * match the input format. */
const TEMPLATE_FIELD_KEYS: ReadonlyArray<keyof ComposerValues> = [
  "title",
  "note_type",
  "category",
  "player_summary",
  "what_happened",
  "why_it_matters",
  "what_to_do_next",
  "tags_raw",
];

/** Canonical string form of a template's tags — the composer renders
 * tags as a comma-separated input (`tags_raw`), so the apply payload
 * and the overwrite-detection comparison both flatten the array to
 * the same shape. Empty template tags yield an empty string. */
function templateTagsRaw(template: CoachTemplate): string {
  return template.tags.join(", ");
}

/** What "untouched" means on a fresh composer. These match the
 * `DEFAULTS` in Composer.tsx. */
const FRESH_DEFAULTS: Record<string, string> = {
  category: "other",
  note_type: "correction",
};

export function TemplateSelector({
  getCurrentValues,
  onApply,
}: TemplateSelectorProps) {
  const [selectedId, setSelectedId] = useState<string>("");
  const [open, setOpen] = useState(false);
  const lastAppliedIdRef = useRef<string | null>(null);
  // M7.5 B5: pending overwrite confirm — when manual edits would
  // be replaced, hold the template + count here and surface
  // `<ConfirmDialog>` instead of `window.confirm`.
  const [pendingOverwrite, setPendingOverwrite] = useState<
    { template: CoachTemplate; editedCount: number } | null
  >(null);

  /** The actual apply — called either directly (no manual edits)
   * or via the ConfirmDialog confirm callback. */
  const commitApply = (template: CoachTemplate) => {
    onApply({
      title: template.title,
      note_type: template.note_type,
      // `template.category` is typed `string` in the registry;
      // narrow it to the composer's closed enum (every shipped
      // template uses an in-set value, so this is a no-op at
      // runtime — it just satisfies the type).
      category: coerceNoteCategory(template.category),
      player_summary: template.player_summary,
      what_happened: template.what_happened,
      why_it_matters: template.why_it_matters,
      what_to_do_next: template.what_to_do_next,
      tags_raw: templateTagsRaw(template),
      __appliedTemplateId: template.id,
    });
    lastAppliedIdRef.current = template.id;
  };

  const handleApply = () => {
    const template = findCoachTemplate(selectedId);
    if (!template) return;

    const current = getCurrentValues();
    const lastTemplate = findCoachTemplate(lastAppliedIdRef.current);

    // Detect manually-edited fields: non-empty + not equal to the
    // last-applied template's value AND not equal to the fresh
    // defaults (those count as untouched on first apply). `tags_raw`
    // gets the same treatment — manual tag edits trigger the confirm
    // alongside title / structured-text edits.
    const manuallyEdited = TEMPLATE_FIELD_KEYS.filter((key) => {
      const value = (current as Record<string, unknown>)[key];
      if (typeof value !== "string") return false;
      const trimmed = value.trim();
      if (!trimmed) return false; // empty = untouched
      // Fresh-composer defaults count as untouched
      if (key in FRESH_DEFAULTS && trimmed === FRESH_DEFAULTS[key]) return false;
      // Compare to last-applied template's value if there was one.
      // tags_raw on the template side is the joined form of the
      // string[] — match the composer's input shape.
      if (lastTemplate) {
        const last =
          key === "tags_raw"
            ? templateTagsRaw(lastTemplate)
            : (lastTemplate as unknown as Record<string, unknown>)[key];
        if (typeof last === "string" && last.trim() === trimmed) return false;
      }
      return true;
    });

    if (manuallyEdited.length > 0) {
      // M7.5 B5: open the styled confirm instead of `window.confirm`.
      setPendingOverwrite({ template, editedCount: manuallyEdited.length });
      return;
    }

    commitApply(template);
  };

  const handleClear = () => {
    setSelectedId("");
    lastAppliedIdRef.current = null;
  };

  return (
    <div
      className="review-template-selector"
      role="group"
      aria-label="Coaching templates"
      data-testid="coach-review-template-selector"
      data-open={open || undefined}
    >
      <button
        type="button"
        className="review-template-toggle"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        data-testid="template-toggle"
      >
        Use a coaching template
        <ChevronDown size={16} aria-hidden />
      </button>
      {open ? (
        <div className="review-template-controls">
          <label className="flex min-w-0 flex-1 flex-col gap-1">
            <span className="text-xs font-display text-ink-muted">Template</span>
            <TkticSelect
              value={selectedId}
              onChange={setSelectedId}
              data-testid="template-select"
              options={[
                { value: "", label: "None — start from scratch" },
                ...COACH_TEMPLATE_GROUPS.flatMap((group) =>
                  templatesInGroup(group).map((t: CoachTemplate) => ({
                    value: t.id,
                    label: `${group} · ${t.label}`,
                  })),
                ),
              ]}
            />
          </label>
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              onClick={handleApply}
              disabled={!selectedId}
              data-testid="template-apply"
              className="rounded bg-accent px-3 py-1.5 text-sm font-display text-accent-ink transition-colors hover:bg-accent-hover disabled:bg-field disabled:text-ink-muted"
            >
              Apply
            </button>
            <button
              type="button"
              onClick={handleClear}
              data-testid="template-clear"
              className="rounded border border-rule bg-canvas px-3 py-1.5 text-sm font-display text-ink-muted transition-colors hover:bg-field hover:text-ink"
            >
              Clear
            </button>
          </div>
        </div>
      ) : null}

      {/* M7.5 B5: styled overwrite confirm. */}
      {pendingOverwrite ? (
        <ConfirmDialog
          title={`Apply "${pendingOverwrite.template.label}"?`}
          message={
            <>
              Your edits in{" "}
              <strong>{pendingOverwrite.editedCount}</strong> field
              {pendingOverwrite.editedCount === 1 ? "" : "s"} will be
              replaced. This can't be undone.
            </>
          }
          confirmLabel="Apply template"
          danger
          onConfirm={() => {
            const t = pendingOverwrite.template;
            setPendingOverwrite(null);
            commitApply(t);
          }}
          onClose={() => setPendingOverwrite(null)}
          data-testid="template-overwrite-confirm"
        />
      ) : null}
    </div>
  );
}

/** Re-export so the parent shell can reset `lastAppliedIdRef` if it
 * wants to (e.g. on Save Note success). */
export { COACH_TEMPLATES };
